import { useState } from 'react';
import { useIntl } from 'react-intl';

import { FormField } from '~/eds/src/components/FormField/FormField';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { api } from '~/redux/api/createApi';
import { Nullable } from '~/types/Nullable';
import { PilotId } from '~/types/PilotId';

import { DOCUMENT_NAME_MAX_LENGTH } from '../constants';

interface Props {
  isVisible: boolean;
  document: { id: PilotId; folderId: PilotId; name: string };
  onHide: () => void;
  onSuccess?: (newName: string) => void;
}
export const RenameActionModal = ({
  document,
  isVisible,
  onHide,
  onSuccess,
}: Props) => {
  const formatNumber = useFormatNumber();
  const { id: documentId, folderId, name } = document;
  const [renameDocument, { isLoading }] =
    api.endpoints.renameDocument.useMutation();
  const [newName, setNewName] = useState<Nullable<string>>(name);
  const { toast } = useToast();
  const intl = useIntl();
  const isNameOverLimit = (newName?.length ?? 0) > DOCUMENT_NAME_MAX_LENGTH;

  const rename = async () => {
    if (newName) {
      try {
        await renameDocument({ documentId, folderId, name: newName }).unwrap();

        toast({
          message: intl.formatMessage({
            id: 'RenameActionModal.successMessage',
            defaultMessage: 'Document renamed successfully',
            description: 'Success message when document is renamed',
          }),
          status: 'success',
        });
        onSuccess?.(newName);
        onHide();
      } catch (error: unknown) {
        toast({ message: (error as Error).message, status: 'danger' });
      }
    }
  };

  return (
    <Modal
      isVisible={isVisible}
      title={intl.formatMessage({
        id: 'RenameActionModal.title',
        defaultMessage: 'Rename Document',
        description: 'Modal title for renaming a document',
      })}
      onHide={onHide}
      primaryAction={{
        text: intl.formatMessage({
          id: 'RenameActionModal.renameButton',
          defaultMessage: 'Rename',
          description: 'Button text to confirm rename action',
        }),
        onClick: rename,
        disabled: isNameOverLimit,
        isLoading,
      }}
    >
      <FormField<string, false>
        name="document-rename"
        placeholder={intl.formatMessage({
          id: 'RenameActionModal.placeholder',
          defaultMessage: 'Enter new document name',
          description: 'Placeholder text for document name input',
        })}
        label={intl.formatMessage({
          id: 'RenameActionModal.label',
          defaultMessage: 'Document Name',
          description: 'Label for document name input field',
        })}
        value={newName}
        input={TextInput}
        onChange={setNewName}
        error={
          isNameOverLimit
            ? intl.formatMessage(
                {
                  id: 'RenameActionModal.nameOverMaxLengthError',
                  defaultMessage:
                    '{count}/{maxLength} (Name cannot be longer than {maxLength} characters)',
                  description:
                    'Error message below input when renaming a document and the length of the name is over the limit. Example: count = 320',
                },
                {
                  count: formatNumber(newName?.length ?? 0),
                  maxLength: formatNumber(DOCUMENT_NAME_MAX_LENGTH),
                },
              )
            : undefined
        }
      />
    </Modal>
  );
};
